import { inject, Injectable, Injector } from '@angular/core';
import { BizEntityTypeCategory, BizEntityFunctionTypes, IBizEntity, IBizEntityType, LoggerService, BizEntityRightsService, RightsOnEntityTypes } from '@optima-stride-ai/core';
import { UILabels, ContextMenuIcons, Routes, MenuConstants } from '../../../shared/enums/app-constants';
import { FormStates, OffCanvasComponents } from '../../../off-canvas/off-canvas-service/off-canvas-form.service';
import { OffcanvasService } from '@coreui/angular';
import { ContextMenuService } from '../../../context-menu/context-menu.service';
import { OffCanvasIDsInDefaultLayout, openOffCanvas, OptimaOffCanvasService, OptimaToasterPlacement, ToastService } from '@optima-stride-ai/optima-components';
import { convertCoreUIIconsToHTMLIcons } from '../../../shared/utils/icon-converter.util';
import { Router } from '@angular/router';
import { GlobalStateService } from '../../../../service/global.service';
import { LibraryExplorerStateService } from './library-explorer-state.service';
import { BizEntityTypeStateStoreService, RelationshipStateStoreService, XappCapHandlerService } from '@optima-stride-ai/optima-app-framework';
import { FormBizEntityComponent } from '@optima-stride-ai/optima-app-framework';
import { createNewBizEntity } from '../../dashboard/dashboard-component/biz-entity.factory';
import { QueryParams } from '../../../../enum/query-params';
import { AppStateService } from '../../../services/state-store/app-state';

/**
 * Service responsible for building and managing library menu items
 * based on entity types and categories
 */
@Injectable({
  providedIn: 'root'
})
export class LibraryMenuService {
  // Single-source list for categories that must remain visible across all routes.
  // To add another globally available functionality in future, add one line here.
  private static readonly ALWAYS_AVAILABLE_ON_ALL_ROUTES = new Set<BizEntityTypeCategory>([
    BizEntityTypeCategory.Tag,
  ]);

  private readonly offCanvasFormService = inject(OptimaOffCanvasService);
  private readonly offCanvasServiceRef = inject(OffcanvasService);
  private readonly injector = inject(Injector);
  private readonly bizEntityRightService = inject(BizEntityRightsService);
  private readonly logger = inject(LoggerService);
  private readonly router = inject(Router);
  private readonly globalStateService = inject(GlobalStateService);
  private readonly explorerState = inject(LibraryExplorerStateService);
  private readonly relationshipState = inject(RelationshipStateStoreService);
  private readonly bizEntityTypeStateStoreService = inject(BizEntityTypeStateStoreService);
  private readonly appStateService = inject(AppStateService);
  readonly xappCapHandlerService = inject(XappCapHandlerService);
  readonly toastService = inject(ToastService)

  private cachedEntityTypes: IBizEntityType[] = [];
  private entityTypesCacheLoaded = false;
  private readonly iconConversionCache = new Map<string, string>();

  constructor() {
    this.primeEntityTypesCache();
  }

  private primeEntityTypesCache(): void {
    this.bizEntityTypeStateStoreService.getState()
      .then((data: any) => {
        if (Array.isArray(data)) {
          this.cachedEntityTypes = data;
          this.entityTypesCacheLoaded = true;
        }
      })
      .catch((error) => {
        this.logger.warn(`Unable to prime entity type cache for context menu: ${error}`);
      });
  }

  private getCachedConvertedIcon(iconName: string): string {
    if (!iconName) {
      return iconName;
    }

    if (this.iconConversionCache.has(iconName)) {
      return this.iconConversionCache.get(iconName)!;
    }

    const convertedIcon = convertCoreUIIconsToHTMLIcons(iconName);
    this.iconConversionCache.set(iconName, convertedIcon);
    return convertedIcon;
  }

  isContextMenuNewItemsCacheReady(): boolean {
    return this.entityTypesCacheLoaded && this.cachedEntityTypes.length > 0;
  }

  /**
   * Build file menu items with callbacks (synchronous version for computed signals)
   */
  buildFileMenuItems(
    onBack?: () => void,
    onPinToDashboard?: (entity?: IBizEntity) => void,
    currentEntity?: IBizEntity,
    onSaveAsTemplate?: (entity?: IBizEntity) => void,
    onSave?: (entity?: IBizEntity) => void
  ): any[] {

    // For the Discussion menu item, we'll initially enable it
    // The async check can be done separately if needed
    const isDisabled = false;

    // Only get dashboard submenu items if entity is operational (document/file)
    const isOperational = currentEntity?.type?.functionType === BizEntityFunctionTypes.Operational;
    const dashboardSubMenuItems = isOperational ? this.getDashboardSubmenuItems(currentEntity) : [];

    const menuItems: any[] = [
      {
        label: UILabels.RENAME,
        icon: ContextMenuIcons.Rename,
        disabled: false,
      },
    ];

    // Only add Save menu item for editable file types
    if (this.isEditableFileType(currentEntity?.type?.category)) {
      menuItems.push({
        label: UILabels.SAVE,
        icon: ContextMenuIcons.Save,
        command: () => {
          this.logger.info(`Save clicked for entity: ${currentEntity?.name || currentEntity?.id || 'undefined'}`);
          if (onSave) {
            onSave(currentEntity);
          }
        },
        disabled: currentEntity?.rights?.update !== true,
      });
    }

    menuItems.push(
      {
        label: UILabels.ADD_TAGS,
        icon: ContextMenuIcons.View,
        disabled: true,
      },
      {
        label: UILabels.PROPERTIES,
        icon: ContextMenuIcons.InfoCircle,
        disabled: true,
      },
      {
        label: UILabels.SAVE_AS_TEMPLATE, icon: ContextMenuIcons.File, command: () => {
          this.logger.info(`Save as template clicked for entity: ${currentEntity?.name || currentEntity?.id || 'undefined'}`);
          if (onSaveAsTemplate) {
            onSaveAsTemplate(currentEntity);
          }
        }
      },
      { separator: true },
      {
        label: UILabels.SHARE,
        icon: ContextMenuIcons.Share,
        disabled: true,
      },
      {
        label: UILabels.PIN_TO_DASHBOARD,
        icon: ContextMenuIcons.Thumbtack,
        items: dashboardSubMenuItems
      },
      { separator: true },
      {
        label: UILabels.DISCUSSION, icon: ContextMenuIcons.Comments,
        command: async () => {
          const res = await this.relationshipState.getState([currentEntity!.id], this.bizEntityTypeStateStoreService, undefined, undefined, true)

          if (!res || res.length === 0) {
            this.logger.info('No discussion channels found');
            return;
          }

          // Collect all VaicChannel entities
          const channels: any[] = [];
          res.forEach((relationship) => {
            relationship.to.forEach((target) => {
              if (target.target.category === BizEntityTypeCategory.VaicChannel) {
                channels.push(target.target.bizEntity);
              }
            });
          });

          // If only one channel, open it directly
          if (channels.length === 1) {
            this.xappCapHandlerService.handleXappEntityClick(channels[0]);
          } else if (channels.length > 1) {
            this.logger.info(`Multiple channels found: ${channels.length}. Use async version for submenu support.`);
            // In synchronous context, just open the first one
            this.xappCapHandlerService.handleXappEntityClick(channels[0]);
          }
        },
        disabled: isDisabled
      },
      {
        label: UILabels.SIGN,
        icon: ContextMenuIcons.SignIn,
        disabled: true,
      },
      {
        label: UILabels.DELETE,
        icon: ContextMenuIcons.Delete,
        disabled: true,
      },
      { separator: true },
      {
        label: UILabels.BACK,
        icon: ContextMenuIcons.ArrowLeft,
        command: () => {
          if (onBack) {
            onBack();
          }
        }
      },
    );

    return menuItems;
  }

  /**
   * Build file menu items with callbacks (async version with discussion check)
   * Use this when you need the async relationship check for the Discussion menu
   */
  async buildFileMenuItemsAsync(
    onBack?: () => void,
    onPinToDashboard?: (entity?: IBizEntity) => void,
    currentEntity?: IBizEntity,
    onSaveAsTemplate?: (entity?: IBizEntity) => void,
    onSave?: (entity?: IBizEntity) => void
  ): Promise<any[]> {
    this.logger.info(`Building file menu items, current entity: ${currentEntity}`);
    let isDisabled = false;
    if (currentEntity && currentEntity.id) {
      // Lazily resolve BizEntityTypeStateStoreService to avoid circular dependency
      // const bizEntityTypeStateStoreService = this.injector.get(BizEntityTypeStateStoreService);
      try {
        const result = await this.relationshipState.getState([currentEntity.id], this.bizEntityTypeStateStoreService, undefined, undefined, true);
        isDisabled = false
      } catch (error) {
        isDisabled = true
      }
    }
    // Only get dashboard submenu items if entity is operational (document/file)
    const isOperational = currentEntity?.type?.functionType === BizEntityFunctionTypes.Operational;
    const dashboardSubMenuItems = isOperational ? this.getDashboardSubmenuItems(currentEntity) : [];

    // Get discussion channel items
    const discussionChannelItems = await this.getDiscussionChannelItems(currentEntity);
    this.logger.info(`Discussion channel items: ${discussionChannelItems.length}`);
    //  const res = this.relationshipState.getState([currentEntity!.id], bizEntityTypeStateStoreService,undefined, undefined, true)

    const menuItems: any[] = [
      {
        label: UILabels.RENAME,
        icon: ContextMenuIcons.Rename,
        disabled: true,
      },
    ];

    // Only add Save menu item for editable file types
    if (this.isEditableFileType(currentEntity?.type?.category)) {
      menuItems.push({
        label: UILabels.SAVE,
        icon: ContextMenuIcons.Save,
        command: () => {
          this.logger.info(`Save clicked for entity: ${currentEntity?.name || currentEntity?.id || 'undefined'}`);
          if (onSave) {
            onSave(currentEntity);
          } else if (currentEntity?.type?.category === BizEntityTypeCategory.HayagrivaFileWhiteboard) {
            this.globalStateService.requestSaveWhiteboard();
          }
        },
        disabled: currentEntity?.rights?.update !== true,
      });
    }

    menuItems.push(
      {
        label: UILabels.ADD_TAGS,
        icon: ContextMenuIcons.View,
        disabled: true,
      },
      {
        label: UILabels.PROPERTIES,
        icon: ContextMenuIcons.InfoCircle,
        disabled: true,
      },
      {
        label: UILabels.SAVE_AS_TEMPLATE, icon: ContextMenuIcons.File, command: () => {
          this.logger.info(`Save as template clicked for entity: ${currentEntity?.name || currentEntity?.id || 'undefined'}`);
          if (onSaveAsTemplate) {
            onSaveAsTemplate(currentEntity);
          }
        },
        // temporarily Disable "Save as template" for images, PDFs, and videos due to unsupported categories
        disabled: currentEntity?.type?.category == BizEntityTypeCategory.HayagrivaFileImage || currentEntity?.type?.category == BizEntityTypeCategory.HayagrivaFilePDF || currentEntity?.type?.category == BizEntityTypeCategory.HayagrivaFileVideo || currentEntity?.type?.category == BizEntityTypeCategory.HayagrivaFileWhiteboard ? true : false
      },
      { separator: true },
      {
        label: UILabels.SHARE,
        icon: ContextMenuIcons.Share,
        disabled: true,
      },
      {
        label: UILabels.PIN_TO_DASHBOARD,
        icon: ContextMenuIcons.Thumbtack,
        items: dashboardSubMenuItems
      },
      { separator: true },
      {
        label: UILabels.DISCUSSION,
        icon: ContextMenuIcons.Comments,
        // If there's only one channel, handle click directly; otherwise show submenu
        ...(discussionChannelItems.length === 1
          ? {
            command: async () => {
              this.logger.info(`Single discussion channel clicked`);
              const res = await this.relationshipState.getState(
                [currentEntity!.id],
                this.bizEntityTypeStateStoreService,
                undefined,
                undefined,
                true
              );
              if (res && res.length > 0) {
                for (const relationship of res) {
                  for (const target of relationship.to) {
                    if (target.target.category === BizEntityTypeCategory.VaicChannel) {
                      const bizEntity = target.target.bizEntity as any;
                      this.xappCapHandlerService.handleXappEntityClick(bizEntity);
                      return;
                    }
                  }
                }
              }
            }
          }
          : { items: discussionChannelItems }),
        disabled: isDisabled || discussionChannelItems.length === 0
      },
      {
        label: UILabels.SIGN,
        icon: ContextMenuIcons.SignIn,
        disabled: true,
      },
      {
        label: UILabels.DELETE,
        icon: ContextMenuIcons.Delete,
        disabled: true,
      },
      { separator: true },
      {
        label: UILabels.BACK,
        icon: ContextMenuIcons.ArrowLeft,
        command: () => {
          if (onBack) {
            onBack();
          }
        }
      },
    );

    return menuItems;
  }

  /**
   * Get dashboard submenu items for pinning
   */
  private getDashboardSubmenuItems(entity?: IBizEntity): any[] {
    this.logger.info(`🔧 Building dashboard submenu, initial entity: ${entity?.name || entity?.id || 'none'}`);

    // Don't capture the entity here - we'll get it fresh from the context menu service
    // when the user actually clicks a dashboard option

    // ⚠️ TEMPORARY PATCH FOR TESTING - Apply default rights if entity lacks rights
    // TODO: Remove this once backend properly sends rights in all entity responses
    const entityWithRights = entity ? {
      ...entity,
      rights: entity.rights
    } as IBizEntity : undefined;

    // Lazily resolve ContextMenuService to avoid circular dependency at construction time.
    const contextMenuService = this.injector.get(ContextMenuService);
    const contextMenu = contextMenuService.getMenu(entityWithRights);

    // Find the "Pin To Dashboard" menu item
    const pinMenuItem = contextMenu.find((item: any) =>
      item.label === UILabels.PIN_TO_DASHBOARD || item.label === UILabels.MOVE_TO_DASHBOARD
    );

    // Return its items if found, otherwise empty array
    if (pinMenuItem && pinMenuItem.items) {
      this.logger.info(`✅ Found ${pinMenuItem.items.length} dashboard options`);
      return pinMenuItem.items;
    }

    this.logger.info('⚠️ No dashboard options found');
    return [];
  }

  /**
   * Get discussion channel submenu items
   */
  private async getDiscussionChannelItems(currentEntity?: IBizEntity): Promise<any[]> {
    if (!currentEntity || !currentEntity.id) {
      return [];
    }

    try {
      const res = await this.relationshipState.getState(
        [currentEntity.id],
        this.bizEntityTypeStateStoreService,
        undefined,
        undefined,
        true
      );

      if (!res || res.length === 0) {
        return [];
      }

      // Collect all VaicChannel entities
      const channels: { bizEntity: any; name: string }[] = [];
      res.forEach((relationship) => {
        relationship.to.forEach((target) => {
          if (target.target.category === BizEntityTypeCategory.VaicChannel) {
            const bizEntity = target.target.bizEntity;
            const name = (typeof bizEntity === 'object' && bizEntity?.name) || 'Channel';
            channels.push({ bizEntity, name });
          }
        });
      });

      // Return submenu items for each channel
      return channels.map((channel) => ({
        label: channel.name,
        icon: ContextMenuIcons.Comment,
        command: () => {
          this.logger.info(`Discussion channel clicked: ${channel.name}`);
          this.xappCapHandlerService.handleXappEntityClick(channel.bizEntity);
        }
      }));
    } catch (error) {
      this.logger.error(`Error fetching discussion channels: ${error}`);
      return [];
    }
  }

  /**
   * Check if a file type category is editable (supports Save operation)
   */
  private isEditableFileType(category?: BizEntityTypeCategory): boolean {
    if (!category) return false;

    const editableCategories = [
      BizEntityTypeCategory.HayagrivaFileDoc,
      BizEntityTypeCategory.HayagrivaFileSpreadsheet,
      BizEntityTypeCategory.HayagrivaFileCSV,
      BizEntityTypeCategory.HayagrivaFileMarkdown,
      BizEntityTypeCategory.HayagrivaFileDrawing,
      BizEntityTypeCategory.HayagrivaFilePresentation,
      BizEntityTypeCategory.HayagrivaFileWhiteboard
    ];

    return editableCategories.includes(category);
  }

  /**
   * File menu items for context menus (backward compatibility)
   * Updated to use async version for backward compatibility
   */
  readonly fileMenuItems: Promise<any[]> = this.buildFileMenuItemsAsync();

  buildMenuItems(
    data: IBizEntityType[],
    onFileUpload: (item: IBizEntity | any, event: any) => void,
    onNewFileCreation: (item: IBizEntity | any, event: any) => void,
    onCreateFolder?: (item: IBizEntity | any, event: any) => void,
    onNewFromTemplate?: (item: IBizEntity | any, category: BizEntityTypeCategory, event: any) => void
  ): any[] {
    if (!data || data.length === 0) {
      return this.getDefaultMenu();
    }
    const excludedCategories = new Set([
      BizEntityTypeCategory.Rights,
      BizEntityTypeCategory.Role,
      BizEntityTypeCategory.TeamMember,
      // BizEntityTypeCategory.Tag,
      BizEntityTypeCategory.AppMeta,
      BizEntityTypeCategory.Team,
      BizEntityTypeCategory.User,
      BizEntityTypeCategory.Tenant,
      BizEntityTypeCategory.ApplicationRootNode,
      BizEntityTypeCategory.Operational,
      BizEntityTypeCategory.HayagrivaFile,
      BizEntityTypeCategory.Structural,
      // BizEntityTypeCategory.Operational,
    ]);

    const routeCategoryFilter = this.getFabRouteCategoryFilter();
    const menuSourceData = (routeCategoryFilter === null
      ? data
      : data.filter((item) =>
        item.category === routeCategoryFilter ||
        item.functionType === BizEntityFunctionTypes.Structural ||
        this.isAlwaysAvailableOnAllRoutes(item.category)
      )
    ).filter((item) => !excludedCategories.has(item.category));

    const menuOrder = [
      BizEntityTypeCategory.HayagrivaFileDoc,
      BizEntityTypeCategory.HayagrivaFileSpreadsheet,
      BizEntityTypeCategory.HayagrivaFileCSV,
      BizEntityTypeCategory.HayagrivaFileMarkdown,
      MenuConstants.SEPARATOR,
      BizEntityTypeCategory.HayagrivaFileDrawing,
      BizEntityTypeCategory.HayagrivaFilePresentation,
      BizEntityTypeCategory.HayagrivaFileWhiteboard,
      MenuConstants.SEPARATOR,
      BizEntityTypeCategory.HayagrivaFilePDF,
      BizEntityTypeCategory.HayagrivaFileImage,
      BizEntityTypeCategory.HayagrivaFileVideo,
      MenuConstants.SEPARATOR,
      BizEntityTypeCategory.HayagrivaLibrary,
      BizEntityTypeCategory.HayagrivaLibFolder,
    ];

    const menuItemsMap: Record<string, any[]> = {};
    let selectedBizEntityId: string | null = null;
    const subscription = this.globalStateService.selectedBizEntityId$.subscribe(id => selectedBizEntityId = id);
    subscription.unsubscribe();
    let mode: number = 0;
    const parentEntity = this.explorerState.explorerAttributes().data.find(d => d.id == selectedBizEntityId);
    let typeRightsMap: Map<number, any> | undefined = undefined;
    if (parentEntity) {
      const typeRightsResult = this.bizEntityRightService.getBizEntityTypeRights(parentEntity);
      mode = typeRightsResult.mode;
      if (typeRightsResult.mode === RightsOnEntityTypes.None) {
        // Note: Toast notification should be shown by the caller, not here
        // This keeps the function pure for use in computed/effect contexts
        return [];
      }
      if (typeRightsResult.typeRights) {
        // Convert Map<string, IRights> to Map<number, IRights>
        typeRightsMap = new Map<number, any>();
        for (const [key, value] of typeRightsResult.typeRights.entries()) {
          typeRightsMap.set(Number(key), value);
        }
      }
    }
    menuSourceData.forEach((item: IBizEntity | any) => {
      const convertedIcon = this.getCachedConvertedIcon(item.icon);
      let disabled = false;
      let rights = item.rights;
      if (typeRightsMap && typeRightsMap.has(Number(item.id))) {
        rights = typeRightsMap.get(Number(item.id));
      }
      // Only disable when create permission is explicitly false.
      if (parentEntity && rights?.create === false && mode !== RightsOnEntityTypes.All) {
        disabled = true;
      }
      if (!menuItemsMap[item.category]) {
        menuItemsMap[item.category] = [];
      }
      menuItemsMap[item.category].push({
        label: item.name,
        icon: convertedIcon,
        items: this.buildSubMenuItems(item, onFileUpload, onNewFileCreation, onCreateFolder, item.category, onNewFromTemplate),
        disabled
      });
    });
    // Build ordered menu with separators; append any categories not covered by menuOrder
    const orderedMenu: any = [];
    const addedCategories = new Set<string>();
    menuOrder.forEach((key) => {
      if (key === MenuConstants.SEPARATOR) {
        orderedMenu.push({ separator: true });
      } else if (menuItemsMap[key]?.length) {
        orderedMenu.push(...menuItemsMap[key]);
        addedCategories.add(String(key));
      }
    });
    Object.entries(menuItemsMap).forEach(([key, items]) => {
      if (!addedCategories.has(key) && items?.length) {
        orderedMenu.push(...items);
      }
    });
    return orderedMenu;
  }

  private getFabRouteCategoryFilter(): BizEntityTypeCategory | null {
    const normalizedUrl = (this.router.url || '').split('?')[0];

    switch (normalizedUrl) {
      case Routes.DOCUMENTS_MARKDOWN:
        return BizEntityTypeCategory.HayagrivaFileMarkdown;
      case Routes.DOCUMENTS_PDF:
        return BizEntityTypeCategory.HayagrivaFilePDF;
      case Routes.DOCUMENTS_DOC:
        return BizEntityTypeCategory.HayagrivaFileDoc;
      case Routes.DOCUMENTS_SPREADSHEET:
        return BizEntityTypeCategory.HayagrivaFileSpreadsheet;
      case Routes.DOCUMENTS_VIDEOS:
        return BizEntityTypeCategory.HayagrivaFileVideo;
      case Routes.DOCUMENTS_IMAGES:
        return BizEntityTypeCategory.HayagrivaFileImage;
      case Routes.DOCUMENTS_PRESENTATIONS:
        return BizEntityTypeCategory.HayagrivaFilePresentation;
      case Routes.DOCUMENTS_DIAGRAMS:
        return BizEntityTypeCategory.HayagrivaFileDrawing;
      default:
        return null;
    }
  }

  private isAlwaysAvailableOnAllRoutes(category?: BizEntityTypeCategory): boolean {
    if (category === undefined || category === null) {
      return false;
    }
    return LibraryMenuService.ALWAYS_AVAILABLE_ON_ALL_ROUTES.has(category);
  }

  /**
   * Build sub-menu items based on category type
   */
  private buildSubMenuItems(
    item: IBizEntity | any,
    onFileUpload: (item: IBizEntity | any, event: any) => void,
    onNewFileCreation: (item: IBizEntity | any, event: any) => void,
    onCreateFolder?: (item: IBizEntity | any, event: any) => void,
    category?: BizEntityTypeCategory,
    onNewFromTemplate?: (item: IBizEntity | any, category: BizEntityTypeCategory, event: any) => void
  ): any[] {
    // Upload-only categories (Image, Video, PDF)
    if (
      item?.category === BizEntityTypeCategory.HayagrivaFileImage ||
      item?.category === BizEntityTypeCategory.HayagrivaFileVideo ||
      item?.category === BizEntityTypeCategory.HayagrivaFilePDF
    ) {
      return [
        {
          label: UILabels.UPLOAD,
          icon: ContextMenuIcons.Link,
          command: (event: any) => {
            onFileUpload(item, event);
          }
        },
        {
          label: UILabels.LINK,
          icon: ContextMenuIcons.Envelope,
          command: (event: any) => {
            this.logger.info(`🔧 Link clicked for: ${item.category} - ${item.name}`);
          },
          disabled: true, // Disable until implemented
        }
      ];
    }

    if (item.category === BizEntityTypeCategory.HayagrivaFileWhiteboard) {
      return [
        {
          label: UILabels.NEW,
          icon: ContextMenuIcons.Plus,
          command: (event: any) => {
            onNewFileCreation(item, event);
          }
        }
      ]
    }

    // Library and Folder categories
    if (
      item.category === BizEntityTypeCategory.HayagrivaLibrary ||
      item.category === BizEntityTypeCategory.HayagrivaLibFolder
    ) {
      return [
        {
          label: item.category === BizEntityTypeCategory.HayagrivaLibrary
            ? UILabels.CREATE_LIBRARY
            : UILabels.CREATE_FOLDER,
          icon: ContextMenuIcons.Plus,
          command: (e: any) => {
            // Use the callback if provided, otherwise fall back to direct openOffCanvas
            if (onCreateFolder) {
              this.logger.info('🔧 Using component callback for folder creation');
              onCreateFolder(item, e);
            } else {
              this.logger.info('🔧 Using service direct call for folder creation');
              item.category === BizEntityTypeCategory.HayagrivaLibrary
                ? openOffCanvas(this.offCanvasFormService, this.offCanvasServiceRef, OffCanvasComponents.AddLibraryFolder, OffCanvasIDsInDefaultLayout.l1_id, 1)
                : openOffCanvas(this.offCanvasFormService, this.offCanvasServiceRef, OffCanvasComponents.AddFolder, OffCanvasIDsInDefaultLayout.l1_id, 1);
            }
          }
        }
      ];
    }

    // Tag category
    if (item.category === BizEntityTypeCategory.Tag) {
      return [
        {
          label: UILabels.NEW,
          icon: ContextMenuIcons.Plus,
          command: () => {
            const selectedType = item as IBizEntityType;
            // Prefer explorer-state selection because it represents the current route/folder context.
            // The global selection stream can be stale during route transitions and may fall back to org root.
            let selectedParentId = this.explorerState.getSelectedParentId?.() || '';
            if (!selectedParentId) {
              let globalSelectedParentId: string | null = null;
              const subscription = this.globalStateService.selectedBizEntityId$.subscribe((id) => {
                globalSelectedParentId = id;
              });
              subscription.unsubscribe();
              selectedParentId = globalSelectedParentId || '';
            }

            const newTagEntity = createNewBizEntity(
              selectedType,
              selectedParentId,
              selectedType.icon || 'cilTag',
              ['Tag']
            );

            this.appStateService.selectedBizEntityTypeForEdit = selectedType;
            this.appStateService.entityFormState = FormStates.New;
            this.offCanvasFormService.dataOfCurrentFormInView = {
              bizEntity: newTagEntity,
              isEditing: false,
            };
            openOffCanvas(this.offCanvasFormService, this.offCanvasServiceRef, FormBizEntityComponent, OffCanvasIDsInDefaultLayout.l1_id, 1, true);
          }
        }
      ]
    }
    // Creatable file categories (Document, Spreadsheet, Markdown, etc.)
    return [
      {
        label: UILabels.NEW,
        icon: ContextMenuIcons.Plus,
        command: (event: any) => {
          onNewFileCreation(item, event);
        }
      },
      {
        label: UILabels.NEW_FROM_TEMPLATE,
        icon: ContextMenuIcons.Plus,
        command: (event: any) => {
          if (onNewFromTemplate && category !== undefined) {
            // Call the component's callback to handle template navigation
            onNewFromTemplate(item, category, event);
          } else {
            // Fallback to direct navigation if no callback provided
            this.router.navigate([Routes.LIBRARIES], {
              queryParams: {
                [QueryParams.IS_TEMPLATE]: true,
                [QueryParams.CATEGORY]: category
              }
            });
          }
        }
      },
      { separator: true },
      {
        label: UILabels.UPLOAD,
        icon: ContextMenuIcons.Upload,
        command: (event: any) => {
          onFileUpload(item, event);
        }
      },
      {
        label: UILabels.LINK,
        icon: ContextMenuIcons.Link,
        command: (event: any) => {
          this.logger.info(`🔧 Link clicked for:- ${item.name}`);
          // Link functionality placeholder
        },
        disabled: true, // Disable until implemented
      }
    ];
  }

  /**
   * Get default menu when no data is available
   */
  private getDefaultMenu(): any[] {
    return [
      {
        label: UILabels.VIEW,
        icon: ContextMenuIcons.View,
        command: (event: any) => {
          /* TODO: Implement view functionality */
        }
      },
      {
        label: UILabels.EDIT,
        icon: ContextMenuIcons.Rename,
        command: (event: any) => {
          /* TODO: Implement edit functionality */
        }
      },
      { separator: true },
      {
        label: UILabels.DELETE,
        icon: ContextMenuIcons.Delete,
        command: (event: any) => {
          /* TODO: Implement delete functionality */
        }
      }
    ];
  }

  buildContextMenuNewItemsSync(
    parentEntity: IBizEntity | any,
    onFileUpload: (item: IBizEntity | any, event: any) => void,
    onNewFileCreation: (item: IBizEntity | any, event: any) => void,
    onCreateFolder?: (item: IBizEntity | any, event: any) => void,
    onNewFromTemplate?: (item: IBizEntity | any, category: BizEntityTypeCategory, event: any) => void
  ): any[] {
    if (!parentEntity || !parentEntity.id) {
      return [];
    }

    if (!this.entityTypesCacheLoaded || this.cachedEntityTypes.length === 0) {
      this.primeEntityTypesCache();
      return [];
    }

    return this.buildContextMenuNewItemsFromTypes(
      parentEntity,
      this.cachedEntityTypes,
      onFileUpload,
      onNewFileCreation,
      onCreateFolder,
      onNewFromTemplate
    );
  }

  buildContextMenuNewItems(
    parentEntity: IBizEntity | any,
    onFileUpload: (item: IBizEntity | any, event: any) => void,
    onNewFileCreation: (item: IBizEntity | any, event: any) => void,
    onCreateFolder?: (item: IBizEntity | any, event: any) => void,
    onNewFromTemplate?: (item: IBizEntity | any, category: BizEntityTypeCategory, event: any) => void
  ): Promise<any[]> {
    if (!parentEntity || !parentEntity.id) {
      return Promise.resolve([]);
    }

    try {
      return this.bizEntityTypeStateStoreService.getState().then((data: any) => {
        if (!Array.isArray(data) || data.length === 0) {
          return [];
        }

        this.cachedEntityTypes = data;
        this.entityTypesCacheLoaded = true;

        return this.buildContextMenuNewItemsFromTypes(
          parentEntity,
          data,
          onFileUpload,
          onNewFileCreation,
          onCreateFolder,
          onNewFromTemplate
        );
      }).catch((error) => {
        this.logger.error(`Error building context menu new items: ${error}`);
        return [];
      });
    } catch (error) {
      this.logger.error(`Error building context menu new items: ${error}`);
      return Promise.resolve([]);
    }
  }

  private buildContextMenuNewItemsFromTypes(
    parentEntity: IBizEntity | any,
    data: IBizEntityType[],
    onFileUpload: (item: IBizEntity | any, event: any) => void,
    onNewFileCreation: (item: IBizEntity | any, event: any) => void,
    onCreateFolder?: (item: IBizEntity | any, event: any) => void,
    onNewFromTemplate?: (item: IBizEntity | any, category: BizEntityTypeCategory, event: any) => void
  ): any[] {
    const excludedCategories = new Set([
      BizEntityTypeCategory.Rights,
      BizEntityTypeCategory.Role,
      BizEntityTypeCategory.TeamMember,
      // BizEntityTypeCategory.Tag,
      BizEntityTypeCategory.AppMeta,
      BizEntityTypeCategory.Team,
      BizEntityTypeCategory.User,
      BizEntityTypeCategory.Tenant,
      BizEntityTypeCategory.ApplicationRootNode,
      BizEntityTypeCategory.Operational,
      BizEntityTypeCategory.HayagrivaFile,
      BizEntityTypeCategory.Structural,
    ]);

    const routeCategoryFilter = this.getFabRouteCategoryFilter();
    const menuSourceData = (routeCategoryFilter === null
      ? data
      : data.filter((item: any) =>
        item.category === routeCategoryFilter ||
        item.functionType === BizEntityFunctionTypes.Structural ||
        this.isAlwaysAvailableOnAllRoutes(item.category)
      )
    ).filter((item: any) => !excludedCategories.has(item.category));

    const menuOrder = [
      BizEntityTypeCategory.HayagrivaFileDoc,
      BizEntityTypeCategory.HayagrivaFileSpreadsheet,
      BizEntityTypeCategory.HayagrivaFileCSV,
      BizEntityTypeCategory.HayagrivaFileMarkdown,
      MenuConstants.SEPARATOR,
      BizEntityTypeCategory.HayagrivaFileDrawing,
      BizEntityTypeCategory.HayagrivaFilePresentation,
      BizEntityTypeCategory.HayagrivaFileWhiteboard,
      MenuConstants.SEPARATOR,
      BizEntityTypeCategory.HayagrivaFilePDF,
      BizEntityTypeCategory.HayagrivaFileImage,
      BizEntityTypeCategory.HayagrivaFileVideo,
      MenuConstants.SEPARATOR,
      BizEntityTypeCategory.HayagrivaLibrary,
      BizEntityTypeCategory.HayagrivaLibFolder,
    ];

    const menuItemsMap: Record<string, any[]> = {};
    const typeRightsResult = this.bizEntityRightService.getBizEntityTypeRights(parentEntity);
    const mode = typeRightsResult.mode;

    if (mode === RightsOnEntityTypes.None) {
      return [];
    }

    let typeRightsMap: Map<number, any> | undefined;
    if (typeRightsResult.typeRights) {
      typeRightsMap = new Map<number, any>();
      for (const [key, value] of typeRightsResult.typeRights.entries()) {
        typeRightsMap.set(Number(key), value);
      }
    }

    menuSourceData.forEach((item: IBizEntity | any) => {
      const convertedIcon = this.getCachedConvertedIcon(item.icon);
      let disabled = false;
      let rights = item.rights;

      if (typeRightsMap && typeRightsMap.has(Number(item.id))) {
        rights = typeRightsMap.get(Number(item.id));
      }

      if (parentEntity && rights?.create === false && mode !== RightsOnEntityTypes.All) {
        disabled = true;
      }

      if (!menuItemsMap[item.category]) {
        menuItemsMap[item.category] = [];
      }

      menuItemsMap[item.category].push({
        label: item.name,
        icon: convertedIcon,
        items: this.buildSubMenuItems(item, onFileUpload, onNewFileCreation, onCreateFolder, item.category, onNewFromTemplate),
        disabled
      });
    });

    const orderedMenu: any[] = [];
    const addedCategories = new Set<string>();

    menuOrder.forEach((key) => {
      if (key === MenuConstants.SEPARATOR) {
        orderedMenu.push({ separator: true });
      } else if (menuItemsMap[key]?.length) {
        orderedMenu.push(...menuItemsMap[key]);
        addedCategories.add(String(key));
      }
    });

    Object.entries(menuItemsMap).forEach(([key, items]) => {
      if (!addedCategories.has(key) && items?.length) {
        orderedMenu.push(...items);
      }
    });

    return orderedMenu;
  }
}
